/**
 * Range-aware wrapper around `API.Data.Robots.byIdWithDetail`. Fetches only the gaps between the
 * requested time window and what's already in the per-robot cache, saving DynamoDB RCUs and client
 * bandwidth when a user widens a previously-loaded window.
 *
 * The fetch is split into 4-hour chunks executed with bounded concurrency so we can report
 * progress to the UI. When a chunk needs both raw + timeline (the common case), a single API call
 * fetches both kinds together — halving the number of round-trips vs running raw and timeline as
 * independent `RangeCache` queries. When the two kinds have diverged cache state (rare — e.g. a
 * past timeline-only fetch), we fall back to per-kind calls so we never re-fetch already-cached data.
 */

import { Robot, type RobotRaw, type RobotTimeline } from "@domain/robot";
import { RangeCache, type Range } from "./rangeCache";
import { API } from "./index";

const CHUNK_MS = 4 * 60 * 60 * 1000; // 4 hours
const CONCURRENCY = 6;

const rawCache = new RangeCache<RobotRaw>({
	timestampOf: (r) => r.timestamp,
});

const timelineCache = new RangeCache<RobotTimeline>({
	timestampOf: (t) => t.start,
});

/** Per-robot snapshot of the most recent base fields (lastUpdated, name, …). Re-stamped on every fetch. */
const baseRobotCache = new Map<string, Robot>();

interface DetailOpts {
	raw?: boolean;
	timeline?: boolean;
	/** Called as each chunk completes; `done` and `total` count chunks (one tick per chunk). */
	onProgress?: (done: number, total: number) => void;
	/** When aborted, the worker pool drains in-flight chunks and rejects with `AbortError`. */
	signal?: AbortSignal;
}

function computeChunks(startMs: number, endMs: number): Array<[number, number]> {
	const chunks: Array<[number, number]> = [];
	for (let s = startMs; s < endMs; s += CHUNK_MS) {
		chunks.push([s, Math.min(s + CHUNK_MS, endMs)]);
	}
	return chunks;
}

function gapsEqual(a: Range[], b: Range[]): boolean {
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) {
		if (a[i][0] !== b[i][0] || a[i][1] !== b[i][1]) return false;
	}
	return true;
}

/**
 * Get a Robot for `[start, end]`, fetching only the missing pieces of `raw` / `timeline`.
 * Returns a fresh Robot object whose arrays are sliced to the requested range (not the full cache).
 */
export async function getRobotInRange(id: string, start: Date, end: Date, opts: DetailOpts): Promise<Robot> {
	const startMs = start.getTime();
	const endMs = end.getTime();

	// Cold path: caller asked for neither raw nor timeline (pure base fetch). Defer to the API directly.
	if (!opts.raw && !opts.timeline) {
		const base = await API.Data.Robots.robots.byIdWithDetail(id, start, end, opts);
		baseRobotCache.set(id, base);
		return base;
	}

	const wantRaw = !!opts.raw;
	const wantTimeline = !!opts.timeline;
	const chunks = computeChunks(startMs, endMs);
	const total = chunks.length;
	let done = 0;
	let cursor = 0;
	const workerCount = Math.min(CONCURRENCY, total);

	const fetchChunk = async (chunk: Range): Promise<void> => {
		const rawGaps = wantRaw ? rawCache.gaps(id, chunk) : [];
		const timelineGaps = wantTimeline ? timelineCache.gaps(id, chunk) : [];

		if (wantRaw && wantTimeline && gapsEqual(rawGaps, timelineGaps)) {
			// Fast path: both kinds want the same intervals → one combined API call per gap.
			for (const gap of rawGaps) {
				const r = await API.Data.Robots.robots.byIdWithDetail(id, new Date(gap[0]), new Date(gap[1]), {
					raw: true,
					timeline: true,
				});
				baseRobotCache.set(id, r);
				rawCache.ingest(id, gap, r.raw ?? []);
				timelineCache.ingest(id, gap, r.timeline ?? []);
			}
			return;
		}

		// Fallback: per-kind calls. Same call count as pre-combine, used when gap sets diverge or only
		// one kind is requested. Each `byIdWithDetail` opts object only includes the flags actually wanted
		// (no explicit `false`) — older callers checked exact opts shape.
		for (const gap of rawGaps) {
			const r = await API.Data.Robots.robots.byIdWithDetail(id, new Date(gap[0]), new Date(gap[1]), {
				raw: true,
			});
			baseRobotCache.set(id, r);
			rawCache.ingest(id, gap, r.raw ?? []);
		}
		for (const gap of timelineGaps) {
			const r = await API.Data.Robots.robots.byIdWithDetail(id, new Date(gap[0]), new Date(gap[1]), {
				timeline: true,
			});
			baseRobotCache.set(id, r);
			timelineCache.ingest(id, gap, r.timeline ?? []);
		}
	};

	await Promise.all(
		Array.from({ length: workerCount }, async () => {
			while (cursor < total) {
				if (opts.signal?.aborted) {
					throw new DOMException("Aborted", "AbortError");
				}
				const i = cursor++;
				await fetchChunk(chunks[i]);
				done++;
				opts.onProgress?.(done, total);
			}
		}),
	);

	let base = baseRobotCache.get(id);
	if (!base) {
		// Defensive: every chunk hit cache and we've never fetched this robot's base fields. Force one.
		base = await API.Data.Robots.robots.byIdWithDetail(id, start, end, opts);
		baseRobotCache.set(id, base);
		return base;
	}

	const out = Object.assign(new Robot(), base);
	if (wantRaw) {
		out.raw = chunks.flatMap((chunk) => rawCache.slice(id, chunk));
	} else {
		delete out.raw;
	}
	if (wantTimeline) {
		out.timeline = chunks.flatMap((chunk) => timelineCache.slice(id, chunk));
	} else {
		delete out.timeline;
	}
	return out;
}

/** Test/debug helper: drop all cached state. */
export function __clearRobotDetailCache(): void {
	rawCache.clear();
	timelineCache.clear();
	baseRobotCache.clear();
}
